import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IoClose, IoHeartOutline, IoHeart, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import Avatar from './Avatar';
import CommentSection from './CommentSection';
import { postApi } from '../services/postApi';
import { formatDistanceToNow } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import './PostImageModal.css';

const PostImageModal = ({ post, currentUserId, imageIndex = 0, onClose, onPostUpdated }) => {
  const { t, i18n } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(imageIndex);
  const [isLiked, setIsLiked] = useState(post.user_has_liked);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const commentSectionRef = useRef(null);
  const locale = i18n.language === 'vi' ? vi : enUS;

  const images = post.media_urls || [];
  const hasMultipleImages = images.length > 1;

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleLike = async () => {
    try {
      if (isLiked) {
        await postApi.unlikePost(post.id);
        setIsLiked(false);
        setLikeCount(prev => Math.max(0, prev - 1));
      } else {
        await postApi.likePost(post.id);
        setIsLiked(true);
        setLikeCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale });
    } catch {
      return timestamp;
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="post-image-modal-overlay" onClick={handleBackdropClick}>
      <div className="post-image-modal">
        <button className="modal-close-btn" onClick={onClose}>
          <IoClose />
        </button>

        <div className="modal-content">
          {/* Left side - Image */}
          <div className="modal-image-container">
            {post.media_types[currentImageIndex] === 'video' ? (
              <video
                src={images[currentImageIndex]}
                controls
                preload="metadata"
                playsInline
                className="modal-image"
              />
            ) : (
              <img src={images[currentImageIndex]} alt="Post" className="modal-image" />
            )}

            {hasMultipleImages && (
              <>
                <button className="modal-nav-btn prev" onClick={handlePrevious}>
                  <IoChevronBack />
                </button>
                <button className="modal-nav-btn next" onClick={handleNext}>
                  <IoChevronForward />
                </button>
                <div className="modal-image-indicator">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>

          {/* Right side - Post details */}
          <div className="modal-details">
            {/* Post header */}
            <div className="modal-post-header">
              <Link to={`/profile/${post.user?.id}`} className="modal-author">
                <Avatar user={post.user} size="small" />
                <div className="modal-author-info">
                  <span className="modal-author-name">{post.user?.display_name}</span>
                  <span className="modal-post-time">{formatTime(post.created_at)}</span>
                </div>
              </Link>
            </div>

            {/* Post caption */}
            {post.content && (
              <div className="modal-caption">
                <Link to={`/profile/${post.user?.id}`} className="caption-author">
                  {post.user?.display_name}
                </Link>
                <span className="caption-text">{post.content}</span>
              </div>
            )}

            {/* Comments section */}
            <div className="modal-comments-container">
              <CommentSection
                ref={commentSectionRef}
                postId={post.id}
                onCommentAdded={() => {}}
                initialVisible={999}
              />
            </div>

            {/* Bottom actions */}
            <div className="modal-actions-container">
              <div className="modal-actions">
                <button
                  className={`modal-action-btn ${isLiked ? 'liked' : ''}`}
                  onClick={handleLike}
                >
                  {isLiked ? <IoHeart /> : <IoHeartOutline />}
                </button>
              </div>

              <div className="modal-stats">
                <span className="modal-likes">
                  {likeCount} {likeCount === 1 ? t('post.like') : t('post.likes')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostImageModal;
